<template>
  <div class="retailer-search-grid">
    <form @submit.prevent="onSubmit">
      <div class="wrapper">
        <input
          :value="query"
          type="text"
          id="retailer-search"
          placeholder="Search for a brand"
          autocomplete="off"
          @input="onSearch"
          @keydown.down.prevent="onArrowDown"
          @keydown.up.prevent="onArrowUp"
          @keydown.enter.prevent="onEnter"
        />

        <!-- Search / Clear icon inside input -->
        <!-- inside wrapper -->
        <span
          class="material-symbols-outlined search-icon search"
          :class="{ visible: !query.length }"
        >
          search
        </span>
        <span
          class="material-symbols-outlined search-icon close"
          :class="{ visible: query.length }"
          @click="clearSearch()"
        >
          close
        </span>

        <!-- Suggestions dropdown -->
        <div v-if="query && filteredRetailers.length > 0" class="suggestions">
          <div
            v-for="(retailer, index) in filteredRetailers"
            :key="retailer.urlName"
            :class="['suggestion-item', { active: index === highlightedIndex }]"
            @mousedown.prevent="selectRetailer(retailer)"
          >
            <div class="logo-wrapper">
              <div v-if="!loadedImageIds.has(retailer.urlName)" class="logo-skeleton"></div>
              <img
                :key="retailer.urlName"
                :src="getOptimizedImageUrl(retailer.imageUrl)"
                :alt="retailer.name"
                width="50"
                height="50"
                loading="eager"
                decoding="async"
                :class="{ loaded: loadedImageIds.has(retailer.urlName) }"
                @load="onImageLoad(retailer.urlName)"
                @error="onImageError($event, retailer.urlName)"
              />
            </div>
            <div class="retailer-info">
              <strong>{{ retailer.name }}</strong>
              <em v-if="retailer.categories.length">
                - {{ retailer.categories[0] }}</em
              >
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import MerchantService from "../services/merchant-service";
import { useTenantConfig } from "../composables/useTenantConfig";

interface Retailer {
  id: number;
  name: string;
  urlName: string;
  includeKeywords: string[];
  categories: string[];
  imageUrl?: string;
  priorityOrder: number;
}
const { settings } = useTenantConfig();
const query = ref("");
const retailerData = ref<Retailer[]>([]);
const highlightedIndex = ref(-1);
const loadedImageIds = ref<Set<string>>(new Set());

// Inline 200x200 grey SVG as fallback
const defaultImage =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect width="200" height="200" fill="%23cccccc"/></svg>';

const cdnPrefix = "https://cdn.rewards4sport.com/cdn-cgi/image/width=100,format=auto,quality=90,fit=scale-down/";

// The CDN resizer can only fetch/transform images hosted on
// *.rewards4sport.com. Many merchant logos are hosted by third parties, so
// Cache the CDN-eligibility check per URL so we don't re-parse the same
// URL on every keystroke re-render.
const cdnEligibilityCache = new Map<string, boolean>();
function isRewards4SportUrl(url: string) {
  const cached = cdnEligibilityCache.get(url);
  if (cached !== undefined) return cached;
  let result: boolean;
  try {
    const { hostname } = new URL(url);
    result = hostname === "rewards4sport.com" || hostname.endsWith(".rewards4sport.com");
  } catch {
    result = false;
  }
  cdnEligibilityCache.set(url, result);
  return result;
}

// Suggestion thumbnails are rendered at ~50px tall, so request a small,
// auto-formatted image from the CDN resizer (2x for retina) instead of the
// full-size original where possible.
function getOptimizedImageUrl(url?: string) {
  if (!url) return defaultImage;
  // The backend may already have wrapped this url with the CDN resizer
  // (e.g. CustomPartnerPage/BettingPartnerPage logos). Wrapping it again
  // produces an invalid nested url that fails to load.
  if (url.includes("/cdn-cgi/image/")) return url;
  return isRewards4SportUrl(url) ? `${cdnPrefix}${url}` : url;
}

async function loadRetailers() {
  try {
    const siteId = settings.value?.tenantSettings?.find((x) => x.name === "CmsSiteId")?.value ?? "";
    const applicationName = settings.value?.tenantSettings?.find((x) => x.name === "ApplicationName")?.value ?? "";
    const clubId = settings.value?.tenantSettings?.find((x) => x.name === "ClubId")?.value ??"";

    const merchants = await MerchantService.getSearchMerchantsData(siteId, applicationName, clubId);

    retailerData.value = merchants.merchants.map((m) => ({
      id: m.merchantId,
      name: m.name,
      urlName: m.urlName,
      includeKeywords: m.includeKeywords || [],
      categories: m.categories || [],
      imageUrl: m.imageUrl || undefined,
      priorityOrder: m.priorityOrder,
    }));
  } catch (err) {
    console.error("Failed to load merchants:", err);
  }
}

onMounted(() => {
  loadRetailers();
});

// Clears the input field
function clearSearch() {
  query.value = ""; // Clear the input
  highlightedIndex.value = -1; // Reset highlighted suggestion
}

// Cap the number of rendered suggestions - beyond this there's no visible
// benefit and it only adds unnecessary DOM/image work per keystroke.
const MAX_SUGGESTIONS = 8;

// Keywords/categories are often short root words (e.g. "bet"). A plain
// `tag.includes(query)` check fails once the user types past that root
// (e.g. "betting"), even though the tag is clearly still relevant. Matching
// in either direction keeps short tags matching longer, related queries.
// Guard against empty/very short tags in the reverse direction though -
// otherwise something like an empty string or single-letter tag would
// trivially match every query (query.includes("") is always true).
const MIN_REVERSE_MATCH_LENGTH = 3;
function isRelatedMatch(tag: string, query: string) {
  const t = tag.trim().toLowerCase();
  if (!t) return false;
  if (t.includes(query)) return true;
  return t.length >= MIN_REVERSE_MATCH_LENGTH && query.includes(t);
}

const filteredRetailers = computed(() => {
  if (!query.value) return [];
  const q = query.value.toLowerCase();

  const nameMatches: Retailer[] = [];
  const keywordMatches: Retailer[] = [];
  const categoryMatches: Retailer[] = [];
  // Dedupe by urlName rather than id - merchantId can be 0/duplicated across
  // merchants (e.g. test data), but urlName is guaranteed unique per merchant.
  const seenUrlNames = new Set<string>();

  for (const r of retailerData.value) {
    if (r.name.toLowerCase().includes(q)) {
      nameMatches.push(r);
      seenUrlNames.add(r.urlName);
    }
  }
  for (const r of retailerData.value) {
    if (!seenUrlNames.has(r.urlName) && r.includeKeywords.some((k) => isRelatedMatch(k, q))) {
      keywordMatches.push(r);
      seenUrlNames.add(r.urlName);
    }
  }
  for (const r of retailerData.value) {
    if (!seenUrlNames.has(r.urlName) && r.categories.some((c) => isRelatedMatch(c, q))) {
      categoryMatches.push(r);
      seenUrlNames.add(r.urlName);
    }
  }

  const merged = [...nameMatches, ...keywordMatches, ...categoryMatches];
  return merged
    .sort((a, b) => a.priorityOrder - b.priorityOrder)
    .slice(0, MAX_SUGGESTIONS);
});

function onSearch(event: Event) {
  // Read the value directly from the native input event rather than relying
  // on v-model, which defers updates until IME composition ends. Android
  // keyboards (e.g. Gboard) treat plain English words as "composing" spans
  // for inline autocorrect, so v-model wouldn't update per-keystroke and the
  // live suggestions would only refresh once a word/space was completed.
  const target = event.target as HTMLInputElement;
  query.value = target.value;
  highlightedIndex.value = -1;
}

function onArrowDown() {
  if (!filteredRetailers.value.length) return;
  highlightedIndex.value =
    (highlightedIndex.value + 1) % filteredRetailers.value.length;
}

function onArrowUp() {
  if (!filteredRetailers.value.length) return;
  highlightedIndex.value =
    highlightedIndex.value <= 0
      ? filteredRetailers.value.length - 1
      : highlightedIndex.value - 1;
}

function onEnter() {
  // Only trigger if a suggestion is selected
  if (
    highlightedIndex.value >= 0 &&
    filteredRetailers.value[highlightedIndex.value]
  ) {
    selectRetailer(filteredRetailers.value[highlightedIndex.value]);
  }
  // Otherwise, do nothing when pressing Enter
}

function selectRetailer(retailer: Retailer) {
  window.location.href = `${retailer.urlName}`;
}

function onImageLoad(urlName: string) {
  loadedImageIds.value.add(urlName);
}

function onImageError(event: Event, urlName: string) {
  const target = event.target as HTMLImageElement | null;
  if (target) {
    target.src = defaultImage;
  }
  loadedImageIds.value.add(urlName);
}

function onSubmit() {
  if (filteredRetailers.value.length > 0) {
    window.location.href = `${filteredRetailers.value[0].urlName}`;
  }
}
</script>

<style scoped>
.wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 30px;
  color: #888;
  cursor: pointer;

  opacity: 0;
  pointer-events: none; /* prevent clicking invisible icon */
}
.search-icon.visible {
  opacity: 1;
  pointer-events: auto;
}

img {
  max-height: 50px;
}

.logo-wrapper {
  position: relative;
  flex: 0 0 auto;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f2f2f2;
  border-radius: 4px;
  overflow: hidden;
}

.logo-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  opacity: 0;
  transition: opacity 0.4s ease-in;
}

.logo-wrapper img.loaded {
  opacity: 1;
}

/* Matches the shimmer skeleton used for the /home retailer cards
   (see RetailerCards.vue) so logos load in consistently across the site. */
.logo-skeleton {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, #eee 25%, #f5f5f5 37%, #eee 63%);
  background-size: 400% 100%;
  animation: shimmer 1.4s ease infinite;
}

@keyframes shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: 0 0;
  }
}

input {
  width: 100%;
  padding: 1rem;
  border: 1px solid #ccc;
  border-radius: 6px;
  outline: none;
  /* Keep at (or above) 16px - anything smaller triggers iOS Safari's
     auto-zoom on focus, which shifts the viewport and can make the live
     suggestions dropdown appear to not show up while typing on mobile. */
  font-size: 16px;
}

.search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
  font-size: 30px;
  cursor: pointer;
  transition: opacity 0.5s ease-in-out;
}

.search-icon.show-x {
  opacity: 1;
}

input:focus {
  border-color: #666;
}

.suggestions {
  position: absolute;
  left: 0;
  right: 0;
  border: 1px solid #ddd;
  margin-top: 4px;
  border-radius: 6px;
  background: white;
  overflow-y: auto;
  /* Must stay above .nav (z-index: 10002 in SiteNav.vue) so the live
     suggestions are never rendered underneath the nav bar on mobile. */
  z-index: 10003;
  max-height: 50vh;
}

.suggestion-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 15px 10px;
  border-bottom: 1px solid #eaeaea;
  cursor: pointer;
}

.suggestion-item a {
  text-decoration: none;
  color: #333;
}

.suggestion-item:hover,
.suggestion-item.active {
  background: #e3e3e3;
}
</style>
